'use client';
import React from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { useApp } from '@/lib/AppContext';
import { t } from '@/lib/i18n';
import { xpChartData } from '@/lib/mockData';
import MetricCard from './MetricCard';
import XpChart from './XpChart';
import AchievementsStrip from './AchievementsStrip';
import NextLessonCard from './NextLessonCard';
import LevelProgressBar from './LevelProgressBar';
import { Flame, Star, Award, BookOpen, TrendingUp, Target } from 'lucide-react';
import Link from 'next/link';

export default function DashboardContent() {
  const { language } = useLanguage();
  const { user, userLevels, userAchievements } = useApp();

  const currentLevel = userLevels?.find((l) => l?.completionPercent < 100 && l?.isUnlocked) ?? userLevels?.[0];
  const nextLesson = currentLevel?.lessons?.find(
    (ls) => ls?.status === 'in_progress' || ls?.status === 'available'
  );
  const totalLessons = userLevels?.reduce((acc, l) => acc + l?.lessons?.length, 0) ?? 0;
  const completedLessons = userLevels?.reduce(
    (acc, l) => acc + l?.lessons?.filter((ls) => ls?.status === 'completed')?.length,
    0
  ) ?? 0;
  const overallProgress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-700 text-foreground">
            {t(language, 'dash_welcome')}, {user?.name?.split(' ')?.[0]} 👋
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {language === 'fr' ?'Continuez votre apprentissage comptable aujourd\'hui' :'Продолжайте изучение бухгалтерии сегодня'}
          </p>
        </div>
        <Link
          href="/learning-path-screen"
          className="hidden sm:flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl text-sm font-600 hover:bg-primary/90 transition-all duration-150 active:scale-95"
        >
          <BookOpen size={16} />
          {t(language, 'dash_continue')}
        </Link>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {/* Hero: Streak — spans 2 cols */}
        <div className="sm:col-span-2 lg:col-span-2">
          <MetricCard
            label={t(language, 'dash_streak')}
            value={`${user?.streak}`}
            unit={t(language, 'common_days')}
            icon={<Flame size={22} className="text-accent" />}
            iconBg="bg-amber-50 border border-amber-200"
            valueSuffix=""
            trend={{ value: 2, positive: true }}
            highlight="streak"
            large
          />
        </div>

        {/* XP */}
        <MetricCard
          label={t(language, 'dash_xp')}
          value={user?.xp?.toLocaleString()}
          icon={<Star size={20} className="text-primary" />}
          iconBg="bg-primary/10 border border-primary/20"
          trend={{ value: 12, positive: true }}
          highlight="xp"
        />

        {/* Rank */}
        <MetricCard
          label={t(language, 'dash_rank')}
          value={user?.rank}
          icon={<Award size={20} className="text-violet-500" />}
          iconBg="bg-violet-50 border border-violet-200"
          isText
        />

        {/* Lessons completed */}
        <MetricCard
          label={t(language, 'dash_lessons')}
          value={`${user?.lessonsCompleted}`}
          icon={<BookOpen size={20} className="text-info" />}
          iconBg="bg-blue-50 border border-blue-200"
          trend={{ value: 3, positive: true }}
        />

        {/* Weekly XP */}
        <MetricCard
          label={t(language, 'dash_weekly_xp')}
          value={user?.weeklyXp?.toLocaleString()}
          icon={<TrendingUp size={20} className="text-success" />}
          iconBg="bg-green-50 border border-green-200"
          trend={{ value: 8, positive: true }}
        />

        {/* Accuracy */}
        <MetricCard
          label={t(language, 'dash_accuracy')}
          value={`${user?.accuracyRate}%`}
          icon={<Target size={20} className="text-danger" />}
          iconBg="bg-red-50 border border-red-200"
          trend={{ value: -2, positive: false }}
          highlight="warning"
        />
      </div>

      {/* Charts + Next lesson */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
        {/* XP Chart — 2 cols */}
        <div className="lg:col-span-2">
          <div className="bg-card rounded-2xl border border-border card-shadow p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-600 text-foreground">{t(language, 'dash_activity')}</h2>
              <span className="text-xs text-muted-foreground">
                {language === 'fr' ? 'Cette semaine' : 'На этой неделе'}
              </span>
            </div>
            <XpChart data={xpChartData} />
          </div>
        </div>

        {/* Next lesson */}
        <div>
          {currentLevel && (
            <NextLessonCard lesson={nextLesson} level={currentLevel} language={language} />
          )}
        </div>
      </div>

      {/* Achievements + Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
        {/* Achievements strip — 2 cols */}
        <div className="lg:col-span-2">
          <AchievementsStrip achievements={userAchievements} language={language} />
        </div>

        {/* Overall progress */}
        <div>
          <div className="bg-card rounded-2xl border border-border card-shadow p-5 h-full">
            <h2 className="text-base font-600 text-foreground mb-4">
              {t(language, 'dash_progress')}
            </h2>
            <div className="space-y-3">
              {userLevels?.slice(0, 5)?.map((level) => (
                <LevelProgressBar key={`level-progress-${level?.id}`} level={level} language={language} />
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground font-500">
                  {language === 'fr' ? 'Progression totale' : 'Общий прогресс'}
                </span>
                <span className="font-700 text-primary">{overallProgress}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}